import type { Styles } from '@adornis/ass/style.js';
import { subscribeAllEntities } from '@adornis/baseql/operations/mongo.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { baseConfig } from '@adornis/config/baseConfig.js';
import { ImageFile } from '@adornis/file-utils/db/images.js';
import { fontReset } from '@adornis/fonts/font-resets.js';
import { projectConfig } from '@chancenwerk/config.js';
import '@chancenwerk/design/components/cr-button.js';
import '@chancenwerk/design/components/cr-checkbox.js';
import '@chancenwerk/design/components/cr-dropdown-selection.js';
import '@chancenwerk/design/components/cr-flex.js';
import '@chancenwerk/design/components/cr-grid.js';
import '@chancenwerk/design/components/cr-image.js';
import '@chancenwerk/design/components/cr-input.js';
import '@chancenwerk/design/components/cr-link.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { from, map, switchMap } from 'rxjs';
import logo from '../client/assets/Logo_ChancenAkademie.jpg';

import './startup.js';

@customElement('cw-workshop-read-more')
export class CWWorkshopReadMore extends ChemistryLitElement {
  @state() private _open = false;

  protected override render(): unknown {
    if (this._open) {
      return html`
        <slot></slot>
        <cr-text
          @click=${() => (this._open = false)}
          ${acss({
            marginTop: this.spacing.lg,
            cursor: 'pointer',
            '&:hover': {
              opacity: 0.7,
            },
          })}
          >Weniger anzeigen...</cr-text
        >
      `;
    }

    return html`<cr-text
      @click=${() => (this._open = true)}
      ${acss({
        cursor: 'pointer',
        '&:hover': {
          opacity: 0.7,
        },
      })}
    >
      Mehr erfahren...
    </cr-text>`;
  }
}

@customElement('cw-workshop-faq-question')
export class CWWorkshopFAQQuestion extends ChemistryLitElement {
  @property({ type: String }) question = '';
  @state() private _open = false;

  override styles(): Styles[] {
    return [
      ...super.styles(),
      {
        ':host > cr-flex': {
          cursor: 'pointer',
          ':hover': {
            opacity: '0.8',
          },
        },
      },
    ];
  }

  protected override render(): unknown {
    return html`<cr-flex
        ${css({
          backgroundColor: '#70ae47',
          borderRadius: '20px',
          color: 'white',
        })}
        padding="md"
        @click=${() => (this._open = !this._open)}
      >
        <cr-text bold>${this.question}</cr-text>
      </cr-flex>
      ${this._open ? html`<slot></slot>` : ''} `;
  }
}

@customElement('cw-workshop-registration')
export class CWWorkshopRegistration extends ChemistryLitElement {
  private _size = new WindowSizeController(this);

  @state() private readonly _workshops = new RXController(
    this,
    // ! fixes cyclic dependency
    from(
      (async () => {
        return await import('@chancenwerk/workshops/entities/Workshop.js');
      })(),
    ).pipe(
      switchMap(Workshop => {
        return subscribeAllEntities(Workshop.Workshop)({
          _id: 1,
          audience: 1,
          roles: 1,
          active: 1,
          title: 1,
          description: 1,
          requiresTraining: 1,
          mediaID: 1,
          media: ImageFile.allFields,
        }).pipe(
          map(w => {
            return w.filter(a => a.active);
          }),
        );
      }),
    ),
  );
  @state() private _workshopRoleTypes = new RXController(
    this,
    // ! fixes cyclic dependency
    from(
      (async () => {
        const WorkshopRoleTypes = await import('@chancenwerk/workshops/entities/Workshop.js');
        return WorkshopRoleTypes.WorkshopRoleTypes;
      })(),
    ),
  );
  @state() private _search = '';

  override styles() {
    return [
      fontReset('Montserrat', 2, 400, 1, 14),
      fontReset('Montserrat', 2, 500, 1, 14),
      fontReset('Montserrat', 2, 700, 1, 14),
      {
        ':host': {},
      },
    ];
  }

  public override render() {
    const lh = { lineHeight: '1.5' };
    const lhBlock = { ...lh, textAlign: 'justify' };
    const liSpacing = acss({
      '& li': {
        marginBottom: this.spacing.md,
      },
    });
    const scrollTo = (id: string) => {
      return () => this.shadowRoot?.getElementById(id)?.scrollIntoView();
    };

    return html`<cr-flex space="xxl" ${css({ paddingBottom: this.spacing.xxl })}>
      <cr-text ${css(lhBlock)}>
        Die CHANCENakademie ist unser eigenes Fortbildungsangebot. Sie widmet sich seit Frühjahr 2022 der
        ganzeinheitlichen Förderung von Kindern und Jugendlichen und der pädagogischen (Weiter-)Qualifizierung von
        ehrenamtlichen Übungsleitungen und festen Mitarbeitenden. Wir führen eigene Workshops durch und arbeiten zudem
        auch mit externen Partner:innen, um verschiedene fachlich relevante Gebiete abzudecken.
      </cr-text>

      <cr-flex space="lg">
        <cr-h3
          ${css({
            color: this.colors.primary,
          })}
        >
          INHALTSVERZEICHNIS
        </cr-h3>

        <cr-grid columns=${this._size.mobile ? '1fr' : '1fr 1fr'}>
          <ul
            ${acss({
              '& li': {
                marginBottom: this.spacing.lg,
              },
            })}
          >
            <li>
              <cr-link href="#uber_uns" @click=${scrollTo('ueber_uns').bind(this)}><cr-text>Über uns</cr-text></cr-link>
            </li>
            <li>
              <cr-link href="#unsere_zielgruppen" @click=${scrollTo('unsere_zielgruppen').bind(this)}
                ><cr-text> Unsere Zielgruppen </cr-text></cr-link
              >
            </li>
            <li>
              <cr-link href="#alle_veranstaltungen" @click=${scrollTo('alle_veranstaltungen').bind(this)}>
                <cr-text> Überblick über die Veranstaltungen </cr-text></cr-link
              >
            </li>
            <li>
              <cr-link href="#unsere_veranstaltungen" @click=${scrollTo('unsere_veranstaltungen').bind(this)}>
                <cr-text>Unsere Veranstaltungen </cr-text>
              </cr-link>
            </li>
            <li>
              <cr-link href="#attendance" @click=${scrollTo('attendance').bind(this)}
                ><cr-text> Hinweise zur Anmeldung und Teilnahme </cr-text></cr-link
              >
            </li>
            <li>
              <cr-link href="#faq" @click=${scrollTo('faq').bind(this)}><cr-text> FAQ </cr-text></cr-link>
            </li>
          </ul>
          <cr-image
            mode="contain"
            .value=${`${projectConfig.get('RAUM_URL')}${logo}`}
            ${css(this._size.mobile ? { height: '400px', order: -1 } : {})}
          >
          </cr-image>
        </cr-grid>
      </cr-flex>

      <cr-flex space="lg">
        <cr-h3
          id="uber_uns"
          ${css({
            color: this.colors.primary,
          })}
        >
          ÜBER UNS
        </cr-h3>

        <cr-text ${css(lhBlock)}>
          Die CHANCENakadamie arbeitet bedarfsorientiert und setzt sich zum Ziel, passgenaue Angebote für verschiedene
          Zielgruppen zu erstellen. Wir bieten daher eine Auswahl an Qualifizierungsmöglichkeiten an. Angeboten werden
          unter anderem Train-the-Trainer Schulungen (TtT), Workshops und Fortbildungen, pädagogische Leitfäden mit
          Handwerkszeug für die Praxis, sowie pädagogisches Material für den Unterricht oder die Lernförderung. In den
          Angeboten für Schüler:innen geht es beispielsweise um die Stärkung von Selbstwirksamkeit, Resilienz und
          Selbst(lern-)Kompetenz von Schüler:innen. Mit unseren weiteren Veranstaltungen möchten wir zum Beispiel die
          interkulturellen kommunikativen Kompetenz fördern, sowie einen sensiblen Umgang mit Sprache und Vielfalt und
          auch die Kompetenz, Kinder und Jugendliche zu motivieren, unterstützen.
        </cr-text>
      </cr-flex>

      <cr-flex space="lg">
        <cr-h3
          id="unsere_zielgruppen"
          ${css({
            color: this.colors.primary,
          })}
        >
          UNSERE ZIELGRUPPEN
        </cr-h3>

        <cr-grid columns=${this._size.mobile ? '1fr' : '1fr 1fr'} space="lg">
          <cr-flex
            space="md"
            padding="lg"
            ${css({
              backgroundColor: '#f5d728',
              borderRadius: '25px',
              color: 'white',
            })}
          >
            <cr-text bold>Schüler:innen</cr-text>
            <cr-text ${css(lhBlock)}>
              Wir bieten Workshops und pädagogisches Material für Unter- und Oberstufenschüler:innen an. Die
              Durchführung der Workshops ist sowohl digital als auch vor Ort möglich. Die Workshops unterstützen eine
              Förderung von Resilienz, Selbstwirksamkeit und Selbst(lern-)kompetenzen der Schüler:innen. Eine
              nachhaltige Verankerung ist durch eine umfangreiche Materialsammlung gegeben.
            </cr-text>
          </cr-flex>

          <cr-flex
            space="md"
            padding="lg"
            ${css({
              backgroundColor: '#e8a03c',
              borderRadius: '25px',
              color: 'white',
            })}
          >
            <cr-text bold>Ehrenamtliche Übungsleitungen</cr-text>
            <cr-text ${css(lhBlock)}>
              Wir bieten für unsere ehrenamtlichen Übungsleitungen gezielte Veranstaltungen für die Einarbeitung sowie
              zur optionalen Weiterbildung an. Für ÜL besteht außerdem die Möglichkeit sich als OS Coach und Trainer:in
              zu qualifizieren, um Workshops selbstständig mit Schüler:innen durchzuführen.
            </cr-text>
          </cr-flex>

          <cr-flex
            space="md"
            padding="lg"
            ${css({
              backgroundColor: '#afca17',
              borderRadius: '25px',
              color: 'white',
            })}
          >
            <cr-text bold>Feste Mitarbeitende</cr-text>
            <cr-text ${css(lhBlock)}>
              Wir bieten gezielte Veranstaltungen für die Einarbeitung unserer MA an und darüber hinaus auch die
              Möglichkeit weiterer Fortbildungsangebote.
            </cr-text>
          </cr-flex>

          <cr-flex
            space="md"
            padding="lg"
            ${css({
              backgroundColor: '#717185',
              borderRadius: '25px',
              color: 'white',
            })}
          >
            <cr-text bold>Kooperationspartner:innen</cr-text>
            <cr-text ${css(lhBlock)}>
              Wir glauben an Kooperation und Collective Impact Partnerschaften und möchten gemeinsam mehr erreichen.
              Deshalb vereinen wir uns mit Expert:innen auf gesellschaftlich relevanten Gebieten, um weitere
              Fortbildungen anbieten zu können. Kooperationsparnter:innen können außerdem auf Anfrage auch an unseren
              Veranstaltungen teilnehmen und/oder wir führen passgenaue Veranstaltung auf Anfrage durch.
            </cr-text>
          </cr-flex>
        </cr-grid>
      </cr-flex>

      ${this._workshopOverview()} ${this._workshopDetails()}

      <cr-flex space="lg">
        <cr-h3
          id="attendance"
          ${css({
            color: this.colors.primary,
          })}
        >
          HINWEISE ZUR ANMELDUNG UND TEILNAHME
        </cr-h3>

        <cr-flex space="md">
          <cr-text bold>Anmeldung und Termine</cr-text>
          <ul ${liSpacing}>
            <li>
              <cr-text ${css(lh)}>
                Die Links zur Anmeldung sind auf der Homepage unter den jeweilige Veranstaltungen zu finden. Zusätzlich
                erhalten feste Mitarbeitende und Übungsleitungen zu Beginn ihrer Tätigkeit von ihren vorgesetzen
                Personen unser gesamtes Veranstaltungsprogramm mitsamt Terminen und Links zur Anmeldung zur Verfügung
                gestellt. Außerdem versenden wir monatlich unseren Newsletter mit anstehenden Veranstaltungen.
              </cr-text>
            </li>
            <li>
              <cr-text ${css(lh)}>
                Schüler:innen müssen sich nicht selbstständig für Workshops anmelden. Die Koordination erfolgt über das
                Schulteam
              </cr-text>
            </li>
            <li>
              <cr-text ${css(lh)}>
                Kooperationspartner:innen können sich für eine Anfrage persönlich bei uns melden
              </cr-text>
            </li>
            <li>
              <cr-text ${css(lh)}>
                Die Anmeldung zu all unseren Veranstaltungen sind verbindlich. Falls du deine Anmeldung doch nicht
                wahrnehmen kannst, sei bitte fair und storniere dein Ticket.
              </cr-text>
            </li>
          </ul>
        </cr-flex>

        <cr-flex space="md">
          <cr-text bold>Hinweise zur Teilnahme</cr-text>
          <ul ${liSpacing}>
            <li>
              <cr-text ${css(lh)}> Bei all unseren Veranstaltungen handelt es sich um digitale Formate. </cr-text>
            </li>
            <li>
              <cr-text ${css(lh)}>
                Unsere Veranstaltungen sind interaktiv gestaltet. Um eine angenehme Lernatmosphäre sowie einen Austausch
                zu gewährleisten, ist eine aktive Mitarbeit in den Veranstaltungen erforderlich. Wir möchten euch daher
                bitten mit Kamera und Mikrofon teilzunehmen. Auch um eine Teilnahme eurerseits bestätigen zu können.
              </cr-text>
            </li>
            <li>
              <cr-text ${css(lh)}>
                Ab einer Verspätung von über 10 Minuten ist eine Teilnahme aufgrund der Fairness gegenüber der anderen
                Teilnehmenen nicht mehr möglich. Wir möchten euch daher bitten, pünktlich zu sein.
              </cr-text>
            </li>
          </ul>
        </cr-flex>

        <cr-flex space="md">
          <cr-text bold>Teilnahmebestätigung</cr-text>
          <ul ${liSpacing}>
            <li>
              <cr-text ${css(lh)}>
                Trainer:innen erhalten nach der Durchführung der Workshops "lernen lernen", "mentale Stärke", "innere
                Kraft", "Vorbild Sein", "Umgang mit Herausforderungen" und "CHANCENzukunft" ein Zertifikat von uns.
                Dafür muss die ausgefüllte TN Liste an events@chancenwerk.org gesendet werden. Für jeden Workshop kann
                jeweils nur ein Zertifikat erlangt werden.
              </cr-text>
            </li>
            <li>
              <cr-text ${css(lh)}>
                Oberstufenschüler:innen erhalten jeweils nach der Teilnahme an den Workshops "Vorbild sein", "Umgang mit
                Herausforderungen" und "CHANCENzukunft" ein Zertifikat. Dieses wird von der Workshopleitung selber
                ausgefüllt und verteilt.
              </cr-text>
            </li>
          </ul>
        </cr-flex>
      </cr-flex>

      <cr-flex space="lg">
        <cr-h4
          ${css({
            color: this.colors.primary,
          })}
        >
          Safe Space
        </cr-h4>

        <cr-text ${css(lhBlock)}>
          Ein sicherer Raum, auch bekannt als "safe space", spielt eine entscheidende Rolle in unseren Veranstaltungen,
          um eine Umgebung zu schaffen, in der sich alle Teilnehmenden respektiert, akzeptiert und gehört fühlen kann.
          Unsere Veranstaltungen streben danach, nicht nur Wissen zu vermitteln, sondern auch eine Gemeinschaft
          aufzubauen, in der Vielfalt geschätzt wird und in der sich jede Person als wertvolles Mitglied fühlen kann.
          Ein sicherer Raum ist somit nicht nur ein Konzept, sondern eine Verpflichtung, die wir ernst nehmen, um die
          positive Entwicklung und Zusammenarbeit aller Teilnehmenden zu fördern.
        </cr-text>

        <cr-h4
          ${css({
            color: this.colors.primary,
          })}
        >
          Unterscheidung: Workshop und Schulung
        </cr-h4>

        <cr-text ${css(lhBlock)}>
          Unsere Veranstaltungen bestehen aus den Formaten „Schulung“ und „Workshop“. <br /><br />
          Unter dem Begriff und Format Schulung verstehen wir die Vorbereitung zur Befähigung, Workshops oder andere
          Tätigkeiten durchzuführen. Vor allem im Rahmen unserer Train-the-Trainer- Schulung liegt der Fokus darauf,
          Übungsleitungen in einen Workshop mit seiner Struktur und seinen Inhalten einzuführen und diese genau zu
          erläutern. Die Übungsleitungen erlangen somit den zusätzlichen Status eines/einer Trainer:in und geben das
          erlernte Wissen dann im Rahmen von Workshops oder Methoden an ihre US und OS in der Lernförderung weiter. Es
          wird also die Theorie erlernt, um dann mit den Kindern und Jugendlichen die Themen in der Praxis
          durchzuführen. Das Ziel und die Verpflichtung sind es somit, dass die Übungsleitungen nach Teilnahme an den
          Schulungen die Workshops selbstständig im Rahmen einer Lernförderung durchführen. <br /><br />
          Unter dem Begriff und Format Workshop verstehen wir die Vermittlung von Wissen und die Entwicklung von
          Kompetenzen, was indirekt in die weitere Tätigkeit getragen wird. Im Rahmen unserer Workshops erlangen die
          Teilnehmenden (Kinder & Jugendliche, Übungsleitungen, feste Mitarbeitende oder Externe) Wissen und
          Kompetenzen, die sie für sich selbst und ihre Tätigkeit aufnehmen und umsetzen können. In den
          Qualifizierungsangeboten für Kinder und Jugendliche geht es dabei u.a. um die Identifizierung mit einer
          Vorbildfunktion, den Umgang mit herausfordernden Situationen und um die Förderung von Selbstwirksamkeit,
          Resilienz und Selbst(lern-)kompetenzen. <br /><br />
          Gerade mit Blick auf die TtT-Schulungen und den dazugehörigen Schüler:innen-Workshops ist diese Unterscheidung
          von großer Bedeutung.
        </cr-text>

        <cr-h4
          ${css({
            color: this.colors.primary,
          })}
        >
          Zusatzqualifikationen für ÜL
        </cr-h4>

        <cr-text ${css(lhBlock)}>
          Alle Übungsleitungen können zusätzlich zu ihrer Rolle an der Schule auch „Trainer:in“ oder „OS-Coach“ werden.
          Jede Rolle hat unterschiedliche Zuständigkeiten und Aufgaben. Genauere Informationen über die
          Zusatzqualifikationen und das Erwerben dieser erhält man in unserer Veranstaltung „Einführung Lernkaskade“.
        </cr-text>
      </cr-flex>

      <cr-flex space="lg">
        <cr-h3
          id="faq"
          ${css({
            color: this.colors.primary,
          })}
        >
          FAQ
        </cr-h3>

        <cw-workshop-faq-question question="Kann ich mich für mehrere Veranstaltungen anmelden?">
          <ul ${liSpacing}>
            <li>
              <cr-text> Ja, kannst du. Wir freuen uns immer über schon bekannte Gesichter. </cr-text>
            </li>
            <li>
              <cr-text>
                Bitte beachte, dass jede Veranstaltung grundsätzlich nur einmal besucht werden kann. Ausnahmen müssen im
                vorhinein mit uns abgesprochen werden.
              </cr-text>
            </li>
          </ul>
        </cw-workshop-faq-question>

        <cw-workshop-faq-question
          question="Ich habe mich zu einer Veranstaltung angemeldet und mir ist etwas dazwischengekommen. Was soll ich tun?"
        >
          <ul ${liSpacing}>
            <li>
              <cr-text>
                Es kann immer passieren, dass einen mal etwas dazwischenkommt. Der Fairness wegen bitten wir dich
                allerdings, so frühzeitig wie möglich deine Bestellung zu stornieren und/oder dich bei
                events@chancenwerk.org zu melden.
              </cr-text>
            </li>
          </ul>
        </cw-workshop-faq-question>

        <cw-workshop-faq-question
          question="Wieso erhalten Trainer:innen für die Durchführung von Workshops ein Zertifiakt?"
        >
          <ul ${liSpacing}>
            <li>
              <cr-text>
                Trainer:innen werden von uns durch dafür vorgesehene Schulungen dazu befähigt, Workshops an Schulen
                durchzuführen. Für diesen Arbeitsaufwand erhalten sie ein Zertifikat und eine besondere Vergütung. Wenn
                du auch Trainer:in werden möchtest, musst du das zuerst mit deiner vorgesetzen Person besprechen und
                unsere Train-the-Trainer Schulungen besuchen.
              </cr-text>
            </li>
          </ul>
        </cw-workshop-faq-question>

        <cw-workshop-faq-question question="Ich habe das Veranstaltungsprogramm nicht erhalten. Was soll ich tun?">
          <ul ${liSpacing}>
            <li>
              <cr-text>
                Bitte erkundige dich bei deiner vorgesetzen Person danach. Sie wird dir das Veranstaltungsprogramm zur
                Verfüfung stellen
              </cr-text>
            </li>
          </ul>
        </cw-workshop-faq-question>

        <cw-workshop-faq-question
          question="Ich habe eine Frage zur CHANCENakademie und/oder zu den Veranstaltungen. An wen soll ich mich wenden?"
        >
          <ul ${liSpacing}>
            <li>
              <cr-text>
                Wenn du ÜL, SuS, PFP oder Trainer:in bist und eine Frage bezüglich der Veranstaltungen hast, melde dich
                gerne jederzeit bei events@chancenwerk.org
              </cr-text>
            </li>
            <li>
              <cr-text> Bei allen anderen Anliegen, wende dich gerne an chancenakademie@chancenwerk.org </cr-text>
            </li>
          </ul>
        </cw-workshop-faq-question>
      </cr-flex>
    </cr-flex>`;
  }

  private _dedupeRoles(roles: string[]) {
    const shorts = roles.map(r => this._roleToShort(r));
    return roles.filter((r, i) => shorts.indexOf(this._roleToShort(r)) === i);
  }

  private _roleToColor(role: string) {
    switch (role) {
      case 'Übungsleitungen (ÜL)':
        return this.colors.accent;
      case 'Feste Mitarbeitende (MA)':
        return this.colors.primary;
      default:
        return 'gray';
    }
  }

  private _roleToShort(role: string) {
    switch (role) {
      case 'Übungsleitungen (ÜL)':
        return 'ÜL';
      case 'Feste Mitarbeitende (MA)':
        return 'MA';
      default:
        return 'KP';
    }
  }

  private _roleSortOrder(role: string) {
    switch (role) {
      case 'Übungsleitungen (ÜL)':
        return 0;
      case 'Feste Mitarbeitende (MA)':
        return 1;
      default:
        return 2;
    }
  }

  private _roleTag(role: string) {
    return html`<cr-flex
      ${css({
        width: '60px',
        height: '30px',
        backgroundColor: this._roleToColor(role),
        borderRadius: '5px',
        justifyContent: 'center',
        color: 'white',
      })}
      crossaxis-center
    >
      <cr-text bold>${this._roleToShort(role)}</cr-text>
    </cr-flex>`;
  }

  private _rolesSplitTag(roles: string[]) {
    const dedupedRoles = this._dedupeRoles(roles).sort((a, b) => this._roleSortOrder(a) - this._roleSortOrder(b));
    return html`<cr-flex horizontal
      >${repeat(
        dedupedRoles,
        r => r,
        (r, i) => html`<cr-flex
          ${css({
            width: `${120 / roles.length}px`,
            height: '30px',
            backgroundColor: this._roleToColor(r),
            borderRadius:
              dedupedRoles.length === 1
                ? '5px'
                : i === 0
                ? '5px 0 0 5px'
                : i === dedupedRoles.length - 1
                ? '0 5px 5px 0'
                : '0',
            justifyContent: 'center',
            color: 'white',
          })}
          crossaxis-center
        >
          <cr-text bold>${this._roleToShort(r)}</cr-text>
        </cr-flex>`,
      )}</cr-flex
    >`;
  }

  private _workshopOverview() {
    return html`<cr-flex space="md">
        <cr-text bold>Anmerkungen & Hinweise</cr-text>
        <cr-text>
          Welche Zielgruppe was besuchen kann, haben wir durch eine Legende der Farb- & Formcodierung kenntlich gemacht.
          Sie soll euch bei der Orientierung helfen:
        </cr-text>
        <cr-flex
          space=${this._size.mobile ? 'md' : ''}
          ?horizontal=${!this._size.mobile}
          ?space-between=${!this._size.mobile}
          ?crossaxis-center=${!this._size.mobile}
        >
          ${repeat(
            this._workshopRoleTypes.value,
            r => r,
            r => {
              return html`<cr-flex horizontal crossaxis-center space="md">
                ${this._roleTag(r)}
                <cr-text>${r}</cr-text>
              </cr-flex>`;
            },
          )}
        </cr-flex>
      </cr-flex>

      <cr-grid columns=${this._size.mobile ? '1fr' : '1fr 1fr'} space="md">
        <cr-h3
          id="alle_veranstaltungen"
          ${css({
            gridColumn: this._size.mobile ? undefined : '1 / span 2',
            color: this.colors.primary,
            paddingBottom: this.spacing.lg,
          })}
        >
          ÜBERBLICK ÜBER UNSERE VERANSTATLUNGEN
        </cr-h3>
        ${repeat(
          this._workshops.value ?? [],
          w => w._id,
          w => {
            return html`<cr-flex horizontal crossaxis-center space="md">
              ${this._rolesSplitTag(w.roles ?? ['Jeder'])}
              <cr-text>${w.title}</cr-text>
            </cr-flex>`;
          },
        )}
      </cr-grid>`;
  }

  private _workshopDetails() {
    return html`<cr-flex space="xl">
      <cr-h3
        id="unsere_veranstaltungen"
        ${css({
          color: this.colors.primary,
        })}
      >
        UNSERE VERANSTALTUNGEN
      </cr-h3>
      <cr-flex ${css({ borderRadius: '15px', border: `5px solid ${this.colors.dark.shade(100)}` })} padding="md">
        <cr-input
          .value=${this._search}
          @value-changed=${e => (this._search = e.detail.value)}
          placeholder="Suche"
        ></cr-input>
      </cr-flex>
      ${repeat(
        (this._workshops.value ?? []).filter(w => (w.title ?? '').toLowerCase().includes(this._search.toLowerCase())),
        w => w._id,
        w => {
          return html`<cr-flex
            space="lg"
            padding="md"
            ${css({ borderRadius: '15px', border: `5px solid ${this.colors.dark.shade(100)}` })}
          >
            ${this._rolesSplitTag(w.roles ?? ['Jeder'])}
            <cr-text ${css({ fontSize: '1.4rem', paddingBotom: this.spacing.lg, color: '#016359', lineHeight: 1.5 })}>
              ${w.title}
            </cr-text>
            <cw-workshop-read-more>
              ${html`<cr-grid columns="${w.mediaID && !this._size.mobile ? '1fr 0.5fr' : '1fr'}" space="md">
                <cr-flex space="md">
                  <cr-text ${css({ lineHeight: 1.5, textAlign: 'justify' })}>${unsafeHTML(w.description)}</cr-text>
                  ${w.audience
                    ? html`<cr-text bold ${css({ lineHeight: 1.5 })}>Zielgruppe:</cr-text>
                        <cr-text>${w.audience}</cr-text>`
                    : ''}
                  <cr-button
                    ${css({ maxWidth: '150px', marginTop: this.spacing.md })}
                    href=${`${baseConfig.get('ROOT_URL')}workshop/registration/${w._id}`}
                    target="_blank"
                  >
                    Anmelden
                  </cr-button>
                </cr-flex>
                ${w.mediaID
                  ? html` <cr-image
                      mode="contain"
                      .value=${w.mediaID}
                      ${css(this._size.mobile ? { height: '280px', order: -1 } : {})}
                    ></cr-image>`
                  : nothing}
              </cr-grid>`}
            </cw-workshop-read-more>
          </cr-flex>`;
        },
      )}
    </cr-flex>`;
  }
}
